import { api } from "./api";

export type CandidatoLite = {
  id: string;
  slug: string;
  tipo: "candidato" | "voto_blanco" | "voto_nulo";
  codigoOrg: string;
  iniciales: string | null;
  nombreCandidato: string;
  nombreOrganizacion: string;
  votosAbsolutos: number;
  pctValidos: number;
  pctEmitidos: number | null;
  ranking: number;
  snapshotAt: string;
  fotoUrl: string | null;
  logoUrl: string | null;
  codigoExpediente: string | null;
};

export type ProcesoSnapshot = {
  idEleccion: number;
  nombre: string;
  fetchedAt: string;
  totalActas: number;
  contabilizadas: number;
  pctContabilizadas: number;
  mesasInstaladas: number | null;
  mesasNoInstaladas: number | null;
  electoresHabiles: number | null;
  totalEmitidos: number;
  totalValidos: number;
  votosBlancos: number;
  votosNulos: number;
  participacion: number;
  fuente: string;
};

export type ListaCandidatos = {
  proceso: ProcesoSnapshot | null;
  total: number;
  candidatos: CandidatoLite[];
};

export type Integrante = {
  posicion: number;
  cargo: string;
  tipoDocumento: string;
  dni: string;
  nombres: string;
  apellidoPaterno: string;
  apellidoMaterno: string | null;
  nombreCompleto: string;
  sexo: string;
  fechaNacimiento: string | null;
  esNativo: string | null;
  estadoCandidato: string | null;
  fotoUrl: string | null;
};

export type PlanGobierno = {
  tipo: "PG" | "RESUMIDO" | "VACIO";
  urlOriginal: string | null;
  urlLocal: string | null;
  bytes: number | null;
  fetchedAt: string;
  filenameOriginal: string | null;
};

export type Plancha = {
  id: string;
  idOrgPolJne: number;
  nombreOrganizacion: string;
  tipoOrgPolitica: string | null;
  logoUrl: string | null;
  codigoExpediente: string | null;
  jeeCreacion: string | null;
  fetchedAt: string;
  integrantes: Integrante[];
  planes: PlanGobierno[];
};

export function fmtBytes(n: number | null | undefined): string {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export type FichaCandidato = {
  candidato: CandidatoLite & {
    dniCandidato: string | null;
    simboloUrl: string | null;
    primerVicepresidente: string | null;
    segundoVicepresidente: string | null;
    fuente: string;
  };
  plancha: Plancha | null;
  eventos: Array<{
    id: string;
    fecha: string;
    tipo: string;
    titulo: string;
    resumen: string;
    estado: string | null;
    documentoId: string | null;
    fuenteUrl: string | null;
    categoria: string;
  }>;
  contexto: {
    anterior: { slug: string; nombreCandidato: string; votosAbsolutos: number; ranking: number } | null;
    siguiente: { slug: string; nombreCandidato: string; votosAbsolutos: number; ranking: number } | null;
    deltaConAnterior: number | null;
    deltaConSiguiente: number | null;
  };
  proceso: {
    fetchedAt: string;
    totalEmitidos: number;
    totalValidos: number;
    electoresHabiles: number | null;
    pctContabilizadas: number;
  } | null;
};

export async function fetchCandidatos(params?: { tipo?: string; sort?: string }): Promise<ListaCandidatos> {
  const { data } = await api.get<ListaCandidatos>("/candidatos", { params });
  return data;
}

export async function fetchCandidato(slug: string): Promise<FichaCandidato> {
  const { data } = await api.get<FichaCandidato>(`/candidatos/${slug}`);
  return data;
}

export function fmtVotos(n: number): string {
  return new Intl.NumberFormat("es-PE").format(n);
}

export function fmtPct(n: number | null | undefined, digits = 3): string {
  if (n === null || n === undefined) return "—";
  return `${n.toFixed(digits)}%`;
}

export function fmtFecha(iso: string): string {
  return new Date(iso).toLocaleDateString("es-PE", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

export function edadEnAnios(fechaNacIso: string): number {
  const nac = new Date(fechaNacIso);
  const now = new Date();
  let y = now.getFullYear() - nac.getFullYear();
  const m = now.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < nac.getDate())) y--;
  return y;
}
